// src/auth/betterAuthClient.ts
//
// Better Auth client for the SUPER-ADMIN portal.
//
// WHY THIS USES A BEARER TOKEN AND THE TENANT PORTAL USES A COOKIE
// This portal is served from a different origin than the API
// (VITE_API_URL=https://api.cmapps.dev/api). A session cookie issued by the API
// would therefore be a third-party cookie here: SameSite=Lax never sends it on
// XHR, and Safari blocks third-party cookies outright. Loosening it to
// SameSite=None would trade a real security property for a mechanism browsers
// are actively removing.
//
// So this portal sends `Authorization: Bearer <session-token>` instead — which
// is also what it already did with Zitadel, so nothing about its transport
// changes. The tenant portal is same-origin and keeps the cookie, which is the
// stronger option where it actually works.
//
// The token is held in sessionStorage, not localStorage: operator access
// reaches every tenant database, and a token that dies with the tab is a
// meaningfully smaller window than one that survives until someone clears
// site data.

import { createAuthClient } from "better-auth/react";
import { twoFactorClient } from "better-auth/client/plugins";
import { passkeyClient } from "@better-auth/passkey/client";
import { API_BASE_URL } from "../api/axios";

const TOKEN_KEY = "cits-operator-session";

export function getOperatorToken(): string | null {
  try {
    return sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setOperatorToken(token: string | null): void {
  try {
    if (token) sessionStorage.setItem(TOKEN_KEY, token);
    else sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    /* private mode — the session simply won't survive a reload */
  }
}

// API_BASE_URL already ends in /api, and the auth handler is mounted under it,
// so the client's base is that same host with the auth path appended.
const baseURL = API_BASE_URL.replace(/\/api\/?$/, "");

export const authClient = createAuthClient({
  baseURL,

  // OPERATORS SIGN IN HERE, NOT AT /api/account.
  //
  // /api/account is the TENANT instance, over auth_user. This portal talks to a
  // separate Better Auth instance over the ops_* tables, with its own signing
  // secret and its own cookie name (cits-ops). The two used to share one table
  // and one unique email index, which meant an address existing on both sides
  // collapsed into a single identity — one password hash, one two-factor
  // secret, one set of passkeys.
  //
  // Pointing this back at /api/account would silently re-tangle them: sign-in
  // would appear to work, against the wrong store, for the wrong population.
  basePath: "/api/operator-account",
  plugins: [twoFactorClient(), passkeyClient()],
  fetchOptions: {
    // COOKIES ARE REQUIRED HERE, despite the bearer token above.
    //
    // Better Auth's two-factor challenge is carried in a signed, temporary
    // cookie (better-auth.two_factor) between sign-in and verify-totp. The
    // plugin has no header or bearer equivalent — it calls setSignedCookie on
    // sign-in and reads that cookie back on verify. Without `credentials`, the
    // browser neither stores nor returns it, and every verification fails with
    // "invalid two factor cookie" no matter how correct the code is.
    //
    // This works TODAY because the portal (stage.cmapps.dev) and the API
    // (cits-api-dev.cmapps.dev) share the registrable domain cmapps.dev, which
    // makes them same-SITE even though they are different origins — SameSite=Lax
    // permits that.
    //
    // ⚠ IT STOPS WORKING IF THE PORTAL AND API END UP ON DIFFERENT REGISTRABLE
    // DOMAINS. Moving the portal to admin.citstime.app while the API stays on
    // cmapps.dev makes these genuinely cross-site: the Lax cookie is not sent,
    // Safari blocks it outright, and operator 2FA breaks. Keep them on one
    // registrable domain (api.citstime.app), or serve the API through the
    // portal's own host so the two are same-origin.
    //
    // The bearer token still carries ordinary API calls; this is only what
    // makes the 2FA handshake possible.
    credentials: "include",
    auth: {
      type: "Bearer",
      token: () => getOperatorToken() ?? "",
    },
    onSuccess: (ctx) => {
      // Better Auth returns the session token in this header when the bearer
      // plugin is enabled. Capture it on sign-in so subsequent calls carry it.
      const token = ctx.response.headers.get("set-auth-token");
      if (token) setOperatorToken(token);
    },
  },
});

// forgetPassword/resetPassword are not surfaced on the client here: operators
// do not self-serve a reset on this portal. A locked-out operator is recovered
// by another operator through the tenant-accounts screen, which keeps the
// recovery path itself behind an authenticated, audited action.
export const { signIn, signOut, useSession, twoFactor, passkey } = authClient;

/**
 * Passkeys are bound to THIS portal's domain, and cannot move with it.
 *
 * A credential enrolled while the portal is served from cmapps.dev stops
 * working the moment it is served from admin.citstime.app — silently, with no
 * error the user can act on. Enrolment is therefore gated on the domain being
 * the final one, rather than left to whoever opens the security page first.
 *
 * Set VITE_PASSKEY_READY=true once the portal is on its permanent hostname.
 */
export function passkeysReady(): boolean {
  if (String(import.meta.env.VITE_PASSKEY_READY) === "true") return true;
  // Production passkeys are bound to citstime.app (PASSKEY_RP_ID), which every
  // *.citstime.app host shares, so moving between those hosts keeps them.
  const host = location.hostname;
  if (host === "citstime.app" || host.endsWith(".citstime.app")) return true;
  // localhost is stable by definition — a dev passkey was never going to move.
  return location.hostname === "localhost" || location.hostname === "127.0.0.1";
}
